<template>
  <div :class="[classes.ctaButton, testClassName]">
    <StyledText
      tag="span"
      :fontId="buttonStyle?.primaryFont"
      :class="[classes.ctaText, 'cta-text', classes.small]"
    >
      {{ text }}
    </StyledText>
  </div>
</template>

<script lang="ts">
import { createUseStyles } from "@fresh8/ad-data-composition/src/theme";
import {
  BrandContext,
  ButtonStyle,
  injectBrand
} from "@fresh8/ad-data-composition/src/brand";
import { defineComponent, computed } from "vue";
import StyledText from "./Text.vue";

import {
  injectTemplateSize,
  LARGE_MPU,
  LMLB
} from "@fresh8/ad-data-composition/src/useTemplateSize";

export default defineComponent({
  name: "CtaButton",
  components: {
    StyledText
  },
  props: {
    href: {
      type: String,
      required: true,
      default: ""
    },
    text: {
      type: String,
      required: true,
      default: "Bet Now"
    },
    buttonId: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const { getButtonStyle, getFontStyle } =
      injectBrand() || ({} as BrandContext);

    const templateContext = injectTemplateSize();

    const fontSize = computed(() => {
      switch (templateContext?.size.value.name) {
        case LARGE_MPU.name:
          return "17px";
        default:
          return "12px";
      }
    });

    const buttonHeight = computed(() => {
      switch (templateContext?.size.value.name) {
        case LARGE_MPU.name:
          return "40px";
        case LMLB.name:
          return "20px";
        default:
          return "26px";
      }
    });

    const buttonStyle = computed(() => {
      return getButtonStyle(props.buttonId);
    });

    const styleComputed = computed(() => ({
      buttonStyle: buttonStyle.value,
      primaryColor: getFontStyle(buttonStyle.value?.primaryFont)?.color,
      secondaryColor: getFontStyle(buttonStyle.value?.secondaryFont)?.color,
      fontSize: fontSize.value,
      buttonHeight: buttonHeight.value
    }));

    const { classesRef } = useStyles(styleComputed);

    return {
      classes: classesRef,
      buttonStyle,
      testClassName: computed(() => "text-style-cta-name")
    };
  }
});

const defaultStyling = {
  backgroundColor: "#fff",
  borderRadius: "",
  borderColor: "",
  borderStyle: "",
  borderWidth: 0,
  autoInteractions: true
};

const { useStyles } = createUseStyles<
  "ctaButton" | "ctaText" | "small",
  {
    buttonStyle: ButtonStyle;
    primaryColor: string;
    secondaryColor: string;
    fontSize: string;
    buttonHeight: string;
  }
>({
  ctaButton: {
    backgroundColor: props =>
      props.buttonStyle.backgroundColor || defaultStyling.backgroundColor,
    borderRadius: props => {
      if (props.buttonStyle.borderRadius === 0) {
        return 0;
      }

      return props.buttonStyle.borderRadius || defaultStyling.borderRadius;
    },
    borderColor: props =>
      props.buttonStyle.borderColor || defaultStyling.borderColor,
    borderStyle: props =>
      props.buttonStyle.borderStyle || defaultStyling.borderStyle,
    borderWidth: props =>
      props.buttonStyle.borderWidth || defaultStyling.borderWidth,
    "&:hover": {
      backgroundColor: props => props.buttonStyle.actions.hover.backgroundColor
    },
    "&:active": {
      backgroundColor: props => props.buttonStyle.actions.active.backgroundColor
    },
    "& > div": {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-around",
      alignItems: "center",
      position: "relative",
      boxSizing: "border-box"
    },
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxSizing: "border-box",
    padding: `7px 12px`,
    height: props => props.buttonHeight
  },
  ctaText: {
    fontSize: props => props.fontSize,
    color: props => props.primaryColor || "#000",
    "offerButton:hover &": {
      color: props => props.buttonStyle.actions.hover.primaryColor
    },
    "offerButton:active &": {
      color: props => props.buttonStyle.actions.active.primaryColor
    }
  }
});
</script>
